import React, { useState, useRef, useEffect } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  DotsThreeOutlineVerticalIcon,
  PushPinIcon,
  EyeSlashIcon,
  TrashIcon,
  SpeakerSlashIcon,
  SignOutIcon,
} from "@phosphor-icons/react";

/**
 * Inline kebab menu shown on every chat message when the current viewer
 * is a stream mod (seller / admin / global mod / appointed stream mod).
 *
 * Actions wired (all hit `/streams/{id}/mod/...` so stream-mods can run
 * them — the existing /mod/* routes still work for global mods too):
 *  - Pin / Unpin
 *  - Hide  (soft-hide, can be unhidden)
 *  - Delete (destructive, soft-deleted but irreversible from UI)
 *  - Mute author (15 min default)
 *  - Kick author from the stream
 *
 * Author-self protection is handled at the call-site: the kebab simply
 * doesn't render on your own messages or on the seller's messages.
 */
export default function ChatMessageModActions({
  streamId,
  message,
  isCurrentlyPinned,
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  // Click-outside to close the menu.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const run = async (label, fn) => {
    setBusy(true);
    try {
      await fn();
      toast.success(label);
      setOpen(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const items = [
    isCurrentlyPinned
      ? {
          key: "unpin",
          label: "Unpin message",
          Icon: PushPinIcon,
          color: "text-amber-700",
          run: () => api.post(`/streams/${streamId}/mod/chat/unpin`),
          successText: "Message unpinned",
        }
      : {
          key: "pin",
          label: "Pin to top",
          Icon: PushPinIcon,
          color: "text-amber-700",
          run: () => api.post(`/streams/${streamId}/mod/chat/${message.id}/pin`),
          successText: "Message pinned",
        },
    {
      key: "hide",
      label: "Hide message",
      Icon: EyeSlashIcon,
      color: "text-zinc-700",
      run: () => api.post(`/streams/${streamId}/mod/chat/${message.id}/hide`),
      successText: "Message hidden",
    },
    {
      key: "delete",
      label: "Delete message",
      Icon: TrashIcon,
      color: "text-red-600",
      run: () => api.delete(`/streams/${streamId}/mod/chat/${message.id}`),
      successText: "Message deleted",
    },
    {
      key: "mute",
      label: "Mute @" + (message.user_name || "user") + " (15 min)",
      Icon: SpeakerSlashIcon,
      color: "text-amber-600",
      run: () =>
        api.post(`/streams/${streamId}/mod/mute/${message.user_id}`, {
          duration_minutes: 15,
        }),
      successText: "User muted",
    },
    {
      key: "kick",
      label: "Kick from stream",
      Icon: SignOutIcon,
      color: "text-red-700",
      run: () => api.post(`/streams/${streamId}/kick/${message.user_id}`),
      successText: "User kicked",
    },
  ];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="p-1 rounded-full hover:bg-zinc-100 text-zinc-500 md:opacity-0 md:group-hover:opacity-100 md:transition-opacity touch-manipulation"
        data-testid={`chat-mod-menu-${message.id}`}
        aria-label="Moderator actions"
      >
        <DotsThreeOutlineVerticalIcon size={14} weight="fill" />
      </button>
      {open && (
        <div
          className="absolute top-7 right-0 z-30 w-56 bg-white border border-zinc-200 rounded-xl shadow-2xl overflow-hidden"
          data-testid={`chat-mod-menu-open-${message.id}`}
        >
          {items.map((it) => (
            <button
              key={it.key}
              type="button"
              onClick={() => run(it.successText, it.run)}
              disabled={busy}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left hover:bg-zinc-50 disabled:opacity-50 ${it.color}`}
              data-testid={`chat-mod-${it.key}-${message.id}`}
            >
              <it.Icon size={14} />
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
